import React, { useState } from "react";
import { useLocalState } from "../hooks/useLocalState";
import { SimulateRequest } from "../types";
import StopPeriodEditor from "./StopPeriodEditor";
import { DateField, NumberField, ReadonlyNumberField, PercentField, SelectField } from "../components/FormFields";

const start = new Date();
const end = new Date();
end.setFullYear(end.getFullYear() + 10);

const DEFAULTS: SimulateRequest = {
  simulationStartDate: start.toISOString().slice(0, 10),
  simulationEndDate: end.toISOString().slice(0, 10),
  marketValue: 10000,
  accountBalance: -10000,
  targetNav: 1000000,
  annualReturn: 0.08,
  annualLoanInterest: 0.04,
  ownContributionPerMonth: 1000,
  monthlySavingsPlanAmount: 1000,
  stopOwnContributionPeriods: [{ start: "2025-01-01", end: "2026-08-01" }],
  stopMonthlySavingsPlanPeriods: [],
  leverage: {
    leverageCapType: "Fixed",
    maxLeverageHardLimitRatio: 0.2,
  }
};

type Props = { onSubmit: (req: SimulateRequest) => Promise<void> };

export default function ConfigForm({ onSubmit }: Props) {
  const [form, setForm] = useLocalState<SimulateRequest>("firesim.config", DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>("");

  // ---- helpers ----
  function update<K extends keyof SimulateRequest>(key: K, value: SimulateRequest[K]) {
    setForm((prev: SimulateRequest) => ({ ...prev, [key]: value }));
  }
  function updateNested(updater: (draft: SimulateRequest) => void) {
    setForm((prev: SimulateRequest) => {
      const draft = structuredClone(prev);
      updater(draft);
      return draft;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onSubmit(form);
    } catch (err: any) {
      setError(err?.message ?? "Unknown error");
    } finally {
      setBusy(false);
    }
  }

  const cur = "€";
  const currentNav = (form.marketValue ?? 0) + (form.accountBalance ?? 0);

  return (
    <form onSubmit={handleSubmit} className="card shadow-sm p-4">
      <h2 className="mb-3">Simulation settings</h2>

      {/* Basics */}
      <h5 className="mt-3 mb-2 text-secondary">Basics</h5>
      <div className="row g-3">
        <DateField
          id="simulationStartDate"
          label="Simulation start date"
          className="col-md-4"
          value={form.simulationStartDate}
          onChange={(v) => update("simulationStartDate", v)}
        />

        <DateField
          id="simulationEndDate"
          label="Simulation end date"
          className="col-md-4"
          value={form.simulationEndDate}
          onChange={(v) => update("simulationEndDate", v)}
        />

        <NumberField
          id="marketValue"
          label={`Market value (${cur})`}
          className="col-md-4"
          value={form.marketValue}
          onChange={(n) => update("marketValue", n)}
        />

        <NumberField
          id="accountBalance"
          label={`Account balance (${cur})`}
          className="col-md-4"
          value={form.accountBalance}
          onChange={(n) => update("accountBalance", n)}
        />

        <ReadonlyNumberField
          id="currentNav"
          label={`Current NAV (${cur})`}
          className="col-md-4"
          value={currentNav}
        />

        <NumberField
          id="targetNav"
          label={`Target NAV (${cur})`}
          className="col-md-4"
          value={form.targetNav}
          onChange={(n) => update("targetNav", n)}
        />

        <PercentField
          id="annualReturn"
          label="Annual return (%)"
          className="col-md-4"
          value={form.annualReturn}
          step={0.01}
          onChange={(fraction) => update("annualReturn", fraction ?? 0)}
        />

        <PercentField
          id="annualLoanInterest"
          label="Loan interest (%)"
          className="col-md-4"
          value={form.annualLoanInterest}
          step={0.01}
          onChange={(fraction) => update("annualLoanInterest", fraction ?? 0)}
        />
      </div>

      {/* Monthly inputs */}
      <h5 className="mt-4 mb-2 text-secondary">Monthly contributions</h5>
      <div className="row g-3">
        <NumberField
          id="ownContributionPerMonth"
          label={`Own contribution / month (${cur})`}
          className="col-md-6"
          value={form.ownContributionPerMonth}
          onChange={(n) => update("ownContributionPerMonth", n)}
        />
        <NumberField
          id="monthlySavingsPlanAmount"
          label={`Savings plan / month (${cur})`}
          className="col-md-6"
          value={form.monthlySavingsPlanAmount}
          onChange={(n) => update("monthlySavingsPlanAmount", n)}
        />
      </div>

      {/* Stop windows */}
      <StopPeriodEditor
        title="Own contribution pause windows"
        periods={form.stopOwnContributionPeriods}
        onChange={(next) => updateNested(d => { d.stopOwnContributionPeriods = next; })}
      />
      <StopPeriodEditor
        title="Monthly savings plan pause windows"
        periods={form.stopMonthlySavingsPlanPeriods}
        onChange={(next) => updateNested(d => { d.stopMonthlySavingsPlanPeriods = next; })}
      />

      {/* Leverage cap */}
      <h5 className="mt-4 mb-2 text-secondary">Leverage cap</h5>
      <div className="row g-3">
        <SelectField
          id="leverageCapType"
          label="Cap type"
          className="col-md-4"
          value={form.leverage.leverageCapType}
          onChange={(val) => updateNested(d => { d.leverage.leverageCapType = val; })}
          options={[
            { value: "None", label: "None" },
            { value: "Fixed", label: "Fixed" }
          ]}
        />

        {form.leverage.leverageCapType === "Fixed" && (
          <PercentField
            id="maxLeverageHardLimitRatio"
            label="Fixed cap (%)"
            className="col-md-4"
            value={form.leverage.maxLeverageHardLimitRatio}
            step={1}
            placeholder="e.g. 50 → 50 %"
            onChange={(fraction) => updateNested(d => { d.leverage.maxLeverageHardLimitRatio = fraction; })}
          />
        )}
      </div>

      <div className="col-md-4" />

      {error && <div className="alert alert-danger mt-3">{error}</div>}

      <div className="mt-3">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Calculating…" : "Simulate"}
        </button>
      </div>
    </form>
  )
}